import React, { type FC, type PropsWithChildren, useEffect, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { CheckOutlined, ContentCopyOutlined } from '@mui/icons-material'

import { createTimeout } from '../../../utils'

import { NotificationSeverity, Translation } from '../../../domain'

import { type BaseCommonProps, CopyIcon, IconButton } from '../common'
import { useTranslator } from '../Internationalization'
import { useAppUsecase, useAppUsecases } from '../State'
import { useIsItemCopied } from './Hooks'

type CopySize = 'small' | 'medium'

type ButtonProps = Readonly<{ disabled?: boolean, onClick?: VoidFunction, 'aria-label'?: string, size?: CopySize }> & BaseCommonProps

type CopyWithSnackbarProps = Pick<ButtonProps, 'className' | 'sx' | 'aria-label' | 'size'> & Readonly<{ button: FC<PropsWithChildren<ButtonProps>> }>
type CopyProps = Omit<CopyWithSnackbarProps, 'button'>
type CopyWithIconsChangeProps = { title?: string, timeout?: number } & BaseCommonProps

const copiedIconStyle: SxProps<Theme> = { color: 'grey.400' }
const customButtonStyle: SxProps<Theme> = { border: 'unset' }

/** Block-level and pulled 2px into the cell padding above and below, so the small 24px button centres on a table cell's 20px text line without making the row taller */
const tableCopyStyle: SxProps<Theme> = { display: 'flex', my: -0.5 }

/**
 * Timeout for the copy icon to be reverted
 * in milliseconds
 */
const ICONS_CHANGE_TIMEOUT = 3200

/**
 * Shared across every copy button so repeated copies (same field twice, or different fields in a
 * row) merge into one toast with a counter badge while visible, instead of stacking a separate
 * "Copied to clipboard" toast per click (`ToastUsecase`'s dedupeKey aggregation).
 */
const COPY_TOAST_DEDUPE_KEY = 'copy-to-clipboard'

const InternalCopyWithSnackbar: FC<PropsWithChildren<CopyWithSnackbarProps>> = ({ children, button: Button, ...props }) => {
    const { copyValueUsecase: copy, toastUsecase } = useAppUsecases()
    const isItemCopied = useIsItemCopied(children)
    const translator = useTranslator()

    return (
        <Button
            aria-label={translator.formatTranslation(Translation.COPY_TO_CLIPBOARD)}
            {...props}
            data-testid={isItemCopied ? 'copied-id' : 'copy-id'}
            onClick={() => {
                copy.invoke(children)
                toastUsecase.show({
                    severity: NotificationSeverity.INFO,
                    title: translator.formatTranslation(Translation.COPIED_TO_CLIPBOARD),
                    dedupeKey: COPY_TOAST_DEDUPE_KEY,
                })
            }}
        >
            <CopyIcon sx={isItemCopied ? copiedIconStyle : undefined} />
        </Button>
    )
}

const CopyIconButton: FC<PropsWithChildren<ButtonProps>> = ({ size = 'medium', ...props }) => <IconButton variant="ghost" size={size} {...props} />

const InternalCopyWithIconsChange: FC<PropsWithChildren<CopyWithIconsChangeProps>> = ({ children, title, timeout = ICONS_CHANGE_TIMEOUT, ...props }) => {
    const copy = useAppUsecase('copyValueUsecase')
    const isItemCopied = useIsItemCopied(children)

    const [isCopyClicked, setCopyClicked] = useState<boolean>(false)

    const onCopy = (): void => {
        copy.invoke(children)
        setCopyClicked(true)
    }

    // Revert the copied state after a timeout
    useEffect(() => createTimeout(() => setCopyClicked(false), timeout), [isCopyClicked])

    return (
        <>
            {isItemCopied && isCopyClicked
                ? (
                    <IconButton variant="ghost" disabled data-testid="copied-btn" {...props}>
                        <CheckOutlined color="success" />
                    </IconButton>
                )
                : (
                    <IconButton variant="ghost" onClick={onCopy} data-testid="copy-btn" {...props}>
                        <ContentCopyOutlined titleAccess={title || ''} />
                    </IconButton>
                )}
        </>
    )
}

/** The copy icon button, which copies its content and shows a "Copied to clipboard" toast */
export const Copy: FC<PropsWithChildren<CopyProps>> = (props) => <InternalCopyWithSnackbar {...props} button={CopyIconButton} />
/** The copy icon button, for a table cell: always small, so the row doesn't grow */
export const TableCopy: FC<PropsWithChildren<Omit<CopyProps, 'size'>>> = ({ sx, ...props }) => (
    <Copy {...props} size="small" sx={{ ...tableCopyStyle, ...sx }} />
)
export const CustomCopy: FC<PropsWithChildren<CopyWithSnackbarProps>> = ({ sx, ...props }) => (
    <InternalCopyWithSnackbar {...props} sx={{ ...customButtonStyle, ...sx }} />
)
export const CopyWithIconsChange: FC<PropsWithChildren<CopyWithIconsChangeProps>> = (props) => <InternalCopyWithIconsChange {...props} />
